"use strict";
/**
 * CONNOX GMBH CONFIDENTIAL
 * ________________________
 *
 * Copyright (c) 2005 - 2020 Connox GmbH, All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Connox GmbH and its suppliers, if any. The intellectual and
 * technical concepts contained herein are proprietary to Connox GmbH and
 * its suppliers and may be covered by German Laws and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * strictly forbidden unless prior written permission is obtained from
 * Connox GmbH.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.isError = exports.CartRequestHandler = void 0;
const BaseRequestHandler_1 = require("../../requestHandler/BaseRequestHandler");
const getRequestConfigWithToken_1 = require("../../requestHandler/getRequestConfigWithToken");
const timer_1 = __importDefault(require("../../logger/timer"));
/**
 * A class for performing requests against the cart api
 */
class CartRequestHandler {
    /**
     * Performs a request that returns the current cart or creates a new one
     * @param {number} shopId
     * @param {string} visitorId
     * @returns {Promise<ICartData>} - the cart data
     */
    static createCart(shopId, visitorId) {
        return __awaiter(this, void 0, void 0, function* () {
            const configuration = yield getRequestConfigWithToken_1.getRequestConfigurationWithToken();
            configuration.data = { visitorId };
            return BaseRequestHandler_1.BaseRequestHandler.performRequest("POST", `/shop/${shopId}/carts`, configuration);
        });
    }
    /**
     * Perform a request to get the cart for a specific visitor id
     * @param shopId
     * @param visitorId
     * @returns {Promise<ICartData|null>} - A promise resolving whether there is a cart or not
     */
    static getCartForVisitorId(shopId, visitorId) {
        return __awaiter(this, void 0, void 0, function* () {
            const configuration = yield getRequestConfigWithToken_1.getRequestConfigurationWithToken();
            configuration.data = { visitorId };
            return yield BaseRequestHandler_1.BaseRequestHandler.performRequestWithPossibleError("GET", `/shop/${shopId}/visitors/${visitorId}/cart`, configuration).then((response) => {
                var _a, _b;
                if (exports.isError(response)) {
                    if (((_b = (_a = response.originalError) === null || _a === void 0 ? void 0 : _a.response) === null || _b === void 0 ? void 0 : _b.status) === 404) {
                        // That 404 error is intended, and this error log comes from axios
                        return null;
                    }
                    throw response;
                }
                return response;
            });
        });
    }
    /**
     * Sends a request that returns the current cart status
     * @param {string} shopId - the id of the shop the cart is associated with
     * @param {string} cartId - the id of the cart
     * @returns {Promise<ICartData>} - resolves to the cart data
     */
    static getCart(shopId, cartId) {
        return __awaiter(this, void 0, void 0, function* () {
            const configuration = yield getRequestConfigWithToken_1.getRequestConfigurationWithToken();
            return BaseRequestHandler_1.BaseRequestHandler.performRequest("GET", `/shop/${shopId}/carts/${cartId}`, configuration);
        });
    }
    /**
     * Performs a request that adds a voucher to a cart
     * @param {string} shopId - the id of the shop the cart is associated with
     * @param {string} cartId - the id of the cart
     * @param {string} voucherCode - the code of the voucher to add
     * @returns {Promise<boolean>} - resolves to whether the operation was successful
     */
    static addVoucher(shopId, cartId, voucherCode) {
        return __awaiter(this, void 0, void 0, function* () {
            const configuration = yield getRequestConfigWithToken_1.getRequestConfigurationWithToken();
            return BaseRequestHandler_1.BaseRequestHandler.performRequest("PUT", `/shop/${shopId}/carts/${cartId}/voucher/${voucherCode}`, configuration);
        });
    }
    /**
     * Performs a request that remove a voucher from a cart
     * * @param {string} shopId - the id of the shop the cart is associated with
     * @param {string} cartId - the id of the cart to remove the voucher from
     * @param {string} voucherCode - the code of the voucher to remove
     * @returns {Promise<boolean>} - resolves to whether the operation was successful
     */
    static removeVoucher(shopId, cartId, voucherCode) {
        return __awaiter(this, void 0, void 0, function* () {
            const configuration = yield getRequestConfigWithToken_1.getRequestConfigurationWithToken();
            return BaseRequestHandler_1.BaseRequestHandler.performRequest("DELETE", `/shop/${shopId}/carts/${cartId}/voucher/${voucherCode}`, configuration);
        });
    }
    /**
     * Send a request that modifies user specific fields on the cart
     * @param {string} shopId - the shop the cart is associated with
     * @param {string} cartId - the id of the cart to modify
     * @param {ICartRequestHandlerPutCartOptions} options - the options with their new values
     * @returns {Promise<ICartData>} - resolves to the new cart data
     */
    static putParams(shopId, cartId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const configuration = yield getRequestConfigWithToken_1.getRequestConfigurationWithToken();
            configuration.data = options;
            return BaseRequestHandler_1.BaseRequestHandler.performRequest("PUT", `/shop/${shopId}/carts/${cartId}`, configuration);
        });
    }
    /**
     * Perform a request that adds an item to a given cart
     * @param {number} shopId - the id of the shop the cart is associated with
     * @param {string} cartId - the id of the cart to add the item to
     * @param {number} itemId - the id of the item to add
     * @param {number} quantity - the quantity of the item
     * @returns {Promise<boolean>} - A promise resolving to whether the action was successful
     */
    static addItem(shopId, cartId, itemId, quantity) {
        return __awaiter(this, void 0, void 0, function* () {
            const configuration = yield getRequestConfigWithToken_1.getRequestConfigurationWithToken();
            configuration.data = { quantity };
            return BaseRequestHandler_1.BaseRequestHandler.performRequest("PUT", `/shop/${shopId}/carts/${cartId}/items/${itemId}`, configuration);
        });
    }
    /**
     * Deletes an item identified by its itemId from the cart
     * @param {number} shopId - the id of the shop the cart is associated with
     * @param {string} cartId - the id of the cart to delete the item from
     * @param {number} itemId - the id of the item to delete
     * @returns {Promise<boolean>} - A promise resolving whether the action was successful
     */
    static removeItem(shopId, cartId, itemId) {
        return __awaiter(this, void 0, void 0, function* () {
            const configuration = yield getRequestConfigWithToken_1.getRequestConfigurationWithToken();
            return BaseRequestHandler_1.BaseRequestHandler.performRequest("DELETE", `/shop/${shopId}/carts/${cartId}/items/${itemId}`, configuration);
        });
    }
}
__decorate([
    timer_1.default("API", "Cart")
], CartRequestHandler, "createCart", null);
__decorate([
    timer_1.default("API", "Cart")
], CartRequestHandler, "getCartForVisitorId", null);
__decorate([
    timer_1.default("API", "Cart")
], CartRequestHandler, "getCart", null);
__decorate([
    timer_1.default("API", "Cart")
], CartRequestHandler, "addVoucher", null);
__decorate([
    timer_1.default("API", "Cart")
], CartRequestHandler, "removeVoucher", null);
__decorate([
    timer_1.default("API", "Cart")
], CartRequestHandler, "putParams", null);
__decorate([
    timer_1.default("API", "Cart")
], CartRequestHandler, "addItem", null);
__decorate([
    timer_1.default("API", "Cart")
], CartRequestHandler, "removeItem", null);
exports.CartRequestHandler = CartRequestHandler;
exports.isError = (possibleError) => {
    return possibleError !== undefined && possibleError.message !== undefined;
};
